import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Toast } from '@common-web/index.js';
import { getErrorString } from '@utils/utils.js';
import { getNotificationsApi } from '@apis/seller-center/notifications';
import { getCurrentUserApi } from '@apis/seller-center/profile-settings';
import dayjs from 'dayjs';
import { strings } from '@constants/strings';
import { mapQueryStringToFilterObject } from '@components-web/filters/helper';

const initialState = {
  notificationsData: [],
  pagination: null,
  success: false,
  loading: false,
  unreadCount: 0,
};

export const fetchNotificationsListing = createAsyncThunk(
  'notifications/listing',
  async (params = '', { getState }) => {
    try {
      const state = getState();
      const currentUser = state?.userData?.userInfo;
      let userId = currentUser?.userProfile?.id;
      if (!userId) {
        const seller = await getCurrentUserApi();
        userId = seller?.current_user?.id;
      }
      const notifications = await getNotificationsApi(params);
      const { queryObj = {} } = mapQueryStringToFilterObject(params);
      return { ...notifications, userId, page: queryObj?.page ?? 1 };
    } catch (err) {
      Toast.error(getErrorString(err));
      throw err;
    }
  }
);

const NotificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    markRead: (state, action) => {
      const index = state?.notificationsData?.findIndex((notification) => notification?.id === action.payload);
      if (index !== -1) {
        state.notificationsData[index].unread = false;
        state.unreadCount -= 1;
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchNotificationsListing.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchNotificationsListing.fulfilled, (state, action) => {
        let unreadCount = 0;
        const trackLabels = { today: null, yesterday: null, older: null };
        const newNotifications = action?.payload?.notifications?.map((notification) => {
          const index = notification?.participants?.findIndex(
            (participant) => participant?.participant_id === action?.payload?.userId
          );
          let isRead = false;
          if (index !== -1) {
            isRead = notification?.participants?.[index]?.is_read ?? false;
            if (!isRead) { unreadCount++; }
          }
          return {
            ...notification,
            unread: !isRead,
          };
        });
        const updatedNotifications = Number(action?.payload?.page) === 1 ? newNotifications : [...state.notificationsData, ...newNotifications];
        state.notificationsData = updatedNotifications?.map((notification) => {
          let label = null;
          const date = dayjs(notification?.created_at);
          if (!trackLabels['today']) {
            if (dayjs().startOf('day').isSame(date, 'day')) {
              label = strings?.today;
              trackLabels['today'] = 1;
              return { ...notification, label };
            }
          }
          if (!trackLabels['yesterday']) {
            if (dayjs().subtract(1, 'day').startOf('day').isSame(date, 'day')) {
              label = strings?.yesterday;
              trackLabels['yesterday'] = 1;
              return { ...notification, label };
            }
          }
          if (!trackLabels['older']) {
            if (dayjs().subtract(1, 'day').startOf('day').isAfter(date)) {
              label = strings?.older;
              trackLabels['older'] = 1;
              return { ...notification, label };
            }
          }
          return { ...notification, label };
        });
        state.unreadCount = action?.payload?.unread_count;
        state.pagination = action?.payload?.pagination;
        state.loading = false;
        state.success = true;
      })
      .addCase(fetchNotificationsListing.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { markRead } = NotificationsSlice.actions;
export default NotificationsSlice.reducer;
